/**
 * Date utility functions.
 *
 * Two families exist on purpose:
 *
 * 1. `formatDate*` — for fields that are semantically a calendar date with no
 *    time component (effective_date, expiration_date, hire_date, etc). These
 *    extract YYYY-MM-DD via regex and never go through a `Date` object, so
 *    the rendered value never shifts with the browser's timezone.
 *
 * 2. `formatTimestamp*` — for fields that are semantically a moment in time
 *    (created_at, updated_at, archived_at, etc). These smart-parse the value
 *    as UTC (appending "Z" when the backend omits the suffix) and render in
 *    the browser's local timezone.
 *
 * Pick the family that matches the field's meaning. When the backend is
 * inconsistent (some fields with `Z`, others without), the smart parser in
 * `parseAsUtc` handles both.
 */

const HAS_TZ_SUFFIX = /([Zz]|[+-]\d{2}:?\d{2}|\s(?:GMT|UTC))$/;
const ISO_DATE_PARTS = /^(\d{4})-(\d{2})-(\d{2})/;
const US_DATE_PARTS = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/;
// RFC 2822 / HTTP date (e.g. "Thu, 18 Jun 2026 00:00:00 GMT") — Flask/werkzeug
// emits this for top-level datetime fields.
const RFC2822_DATE_PARTS = /^[A-Za-z]{3},\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/;

const MONTH_NAMES_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const EMPTY = "—";

type DateInput = string | Date | null | undefined;

/**
 * Smart UTC parse. If the string already has a timezone suffix, it's
 * respected; otherwise we treat it as UTC (which is what our backends emit
 * even when they omit the `Z` suffix). Date inputs pass through unchanged.
 */
function parseAsUtc(value: string | Date): Date {
  if (value instanceof Date) return value;
  // Letter-leading (RFC 2822 / human-readable): treat a zone-less string as UTC
  // too, else `new Date` parses it in the browser's zone and the day shifts per
  // user (BAR-233). Numeric ISO likewise gets a "Z" when it omits the offset.
  if (/^[A-Za-z]/.test(value)) {
    return new Date(HAS_TZ_SUFFIX.test(value) ? value : `${value} UTC`);
  }
  return new Date(HAS_TZ_SUFFIX.test(value) ? value : `${value}Z`);
}

/**
 * Extracts year/month/day parts from a string without going through a `Date`
 * object — so the value never shifts with the browser's timezone. Accepts
 * ISO (`YYYY-MM-DD[...]`) and US (`MM/DD/YYYY` or `MM-DD-YYYY`) formats.
 * Returns `null` when the string doesn't match either.
 */
function extractDateParts(
  value: string,
): { y: string; m: string; d: string } | null {
  const iso = ISO_DATE_PARTS.exec(value);
  if (iso) return { y: iso[1], m: iso[2], d: iso[3] };
  const us = US_DATE_PARTS.exec(value);
  if (us) {
    return {
      y: us[3],
      m: us[1].padStart(2, "0"),
      d: us[2].padStart(2, "0"),
    };
  }
  const rfc = RFC2822_DATE_PARTS.exec(value);
  if (rfc) {
    const monthIndex = MONTH_NAMES_SHORT.indexOf(
      rfc[2][0].toUpperCase() + rfc[2].slice(1, 3).toLowerCase(),
    );
    if (monthIndex !== -1) {
      return {
        y: rfc[3],
        m: String(monthIndex + 1).padStart(2, "0"),
        d: rfc[1].padStart(2, "0"),
      };
    }
  }
  return null;
}

function partsFromDate(date: Date): { y: string; m: string; d: string } {
  return {
    y: String(date.getUTCFullYear()),
    m: String(date.getUTCMonth() + 1).padStart(2, "0"),
    d: String(date.getUTCDate()).padStart(2, "0"),
  };
}

function partsFromInput(value: DateInput): { y: string; m: string; d: string } | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : partsFromDate(value);
  }
  return extractDateParts(value);
}

// ---------------------------------------------------------------------------
// "Now" helpers
// ---------------------------------------------------------------------------

export function getCurrentYear(): number {
  return new Date().getFullYear();
}

export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function getFormattedDate(): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function getFormattedTime(): string {
  return new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Pure-date formatters (regex-based, no Date, no timezone shifting)
// ---------------------------------------------------------------------------

/** "May 21, 2026". For calendar-date fields like `effective_date`. */
export function formatDate(value: DateInput): string {
  const parts = partsFromInput(value);
  if (!parts) return EMPTY;
  const month = MONTH_NAMES_LONG[Number(parts.m) - 1];
  return `${month} ${Number(parts.d)}, ${parts.y}`;
}

/** "May 21, 2026" with short month. For calendar-date fields. */
export function formatDateMedium(value: DateInput): string {
  const parts = partsFromInput(value);
  if (!parts) return EMPTY;
  const month = MONTH_NAMES_SHORT[Number(parts.m) - 1];
  return `${month} ${Number(parts.d)}, ${parts.y}`;
}

/** "05/21/2026". For calendar-date fields. */
export function formatDateShort(value: DateInput): string {
  const parts = partsFromInput(value);
  if (!parts) return EMPTY;
  return `${parts.m}/${parts.d}/${parts.y}`;
}

// ---------------------------------------------------------------------------
// Timestamp formatters (smart UTC parse → render in browser's local TZ)
// ---------------------------------------------------------------------------

/** "May 21, 2026" rendered in local time. For timestamps. */
export function formatTimestamp(value: DateInput): string {
  if (value == null || value === "") return EMPTY;
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** "May 21, 2026" with short month, in local time. For timestamps. */
export function formatTimestampMedium(value: DateInput): string {
  if (value == null || value === "") return EMPTY;
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** "05/21/2026". Renders in the browser's local timezone, or in `timeZone` (an IANA zone) when given. For timestamps. */
export function formatTimestampShort(
  value: DateInput,
  options?: { timeZone?: string },
): string {
  if (value == null || value === "") return EMPTY;
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: options?.timeZone,
  });
}

/** "May 21, 2026, 2:30 PM" rendered in local time. For timestamps. */
export function formatTimestampLong(value: DateInput): string {
  if (value == null || value === "") return EMPTY;
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "2 days ago" / "just now". For timestamps. */
export function getRelativeTime(value: string | Date): string {
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatTimestamp(date);
}

// ---------------------------------------------------------------------------
// Form input helper
// ---------------------------------------------------------------------------

/**
 * "YYYY-MM-DD" suitable for `<input type="date">` / `<DatePicker />`.
 *
 * For pure-date inputs, extracts the parts directly. For values that include
 * a time component, smart-parses as UTC and emits the UTC calendar date —
 * so a naive backend datetime like `"2026-05-21T02:00:00"` always becomes
 * `"2026-05-21"` regardless of the browser's timezone.
 */
export function formatDateForInput(value: string | Date): string {
  if (typeof value === "string") {
    const parts = extractDateParts(value);
    if (parts) return `${parts.y}-${parts.m}-${parts.d}`;
  }
  const date = parseAsUtc(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = partsFromDate(date);
  return `${parts.y}-${parts.m}-${parts.d}`;
}
